import { useCallback, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  bucketConfirmMessage,
  categoryState,
  categoryStateLabel,
  fetchRolePermissionMatrix,
  filterMatrix,
  isProtectedLeaf,
  matrixLeafCount,
  MATRIX_ROLES,
  MATRIX_ROLE_HINTS,
  MATRIX_ROLE_LABELS,
  PROTECTED_LEAF_NOTE,
  setBucketPermissions,
  setRolePermission,
  type MatrixBucket,
  type MatrixRole,
  type RolePermissionMatrixLoad,
} from "../data/role-permission-matrix";
import { useReadModel } from "../data/useReadModel";

// PRD-001AE AE3: Group Permissions.
//
// This edits the RBAC layer only: which permission KEY each membership role carries. It is not the
// runtime policy layer (duty tracking, mobile flags, alert channels), which lives on its own Admin
// panels and is never called "permissions" here (AC-001AE.5), and it is not role ASSIGNMENT, which
// stays on Members and is AE4's Individual tab.
//
// The bucket map is the host's. Rows arrive already bucketed and ordered by the list RPC, which
// refuses outright if the live key catalog and the PRD-001AE map disagree, so every live key
// appears exactly once (AC-001AE.1) as a server guarantee rather than a client-side copy.

export function GroupPermissionsPanel({
  client,
  accountId,
  canView = false,
  canManage = false,
  onPermissionsChanged,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canView?: boolean;
  canManage?: boolean;
  /** Re-read the caller's own capability keys so nav gates follow a matrix edit. */
  onPermissionsChanged?: () => void;
  pollMs?: number;
}) {
  if (!canView) {
    return null;
  }
  return (
    <GroupPermissionsPanelInner
      client={client}
      accountId={accountId}
      canManage={canManage}
      {...(onPermissionsChanged !== undefined ? { onPermissionsChanged } : {})}
      {...(pollMs !== undefined ? { pollMs } : {})}
    />
  );
}

function GroupPermissionsPanelInner({
  client,
  accountId,
  canManage,
  onPermissionsChanged,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  onPermissionsChanged?: () => void;
  pollMs?: number;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [busyCell, setBusyCell] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  /**
   * A category toggle is armed before it runs. There is no bucket batch RPC, so this is N separate
   * writes; an in-panel confirm matches the rest of the console and, unlike `window.confirm`,
   * behaves the same inside the Capacitor shell.
   */
  const [pendingBucket, setPendingBucket] = useState<{
    readonly bucketKey: string;
    readonly next: boolean;
    readonly role: MatrixRole;
  } | null>(null);

  const { status, data, error } = useReadModel<RolePermissionMatrixLoad>(
    () => fetchRolePermissionMatrix(client, accountId),
    [client, accountId, reloadNonce],
    pollMs !== undefined ? { pollMs } : {},
  );

  const buckets = data !== null && data.status === "ready" ? data.buckets : [];
  const visible = useMemo(() => filterMatrix(buckets, query), [buckets, query]);
  const totalLeaves = matrixLeafCount(buckets);
  const pendingConfirm = useMemo(() => {
    if (pendingBucket === null) return null;
    const bucket = buckets.find((candidate) => candidate.bucketKey === pendingBucket.bucketKey);
    // A reload that dropped the bucket (search change, host edit) drops the armed confirm with it
    // rather than leaving a button that would act on something no longer on screen.
    return bucket === undefined ? null : { bucket, next: pendingBucket.next, role: pendingBucket.role };
  }, [buckets, pendingBucket]);

  /** Every write funnels through here so the reload and the nav refresh cannot be forgotten. */
  const afterWrite = useCallback(
    (message: string | null, reason: string | null) => {
      setFailure(reason);
      setReceipt(reason === null ? message : null);
      setReloadNonce((nonce) => nonce + 1);
      // The editor may have just changed their OWN capabilities. Re-read them so the nav stops
      // offering what the server has started refusing.
      onPermissionsChanged?.();
    },
    [onPermissionsChanged],
  );

  const toggleLeaf = useCallback(
    async (bucket: MatrixBucket, permissionKey: string, role: MatrixRole, next: boolean) => {
      if (!canManage || busyCell !== null) return;
      if (isProtectedLeaf(role, permissionKey, next)) {
        setFailure(PROTECTED_LEAF_NOTE);
        setReceipt(null);
        return;
      }
      const cellId = `${bucket.bucketKey}:${permissionKey}:${role}`;
      setBusyCell(cellId);
      setFailure(null);
      setReceipt(null);
      const outcome = await setRolePermission(client, accountId, role, permissionKey, next);
      setBusyCell(null);
      if (!outcome.ok) {
        afterWrite(null, outcome.reason);
        return;
      }
      afterWrite(
        outcome.changed
          ? `${MATRIX_ROLE_LABELS[role]} ${next ? "granted" : "no longer has"} ${permissionKey}.`
          : "That permission was already in this state.",
        null,
      );
    },
    [accountId, afterWrite, busyCell, canManage, client],
  );

  const toggleBucket = useCallback(
    async (bucket: MatrixBucket, role: MatrixRole, next: boolean) => {
      if (!canManage || busyCell !== null) return;
      const cellId = `${bucket.bucketKey}:*:${role}`;
      setPendingBucket(null);
      setBusyCell(cellId);
      setFailure(null);
      setReceipt(null);
      const result = await setBucketPermissions(
        client,
        accountId,
        role,
        bucket.leaves.map((leaf) => leaf.permissionKey),
        next,
      );
      setBusyCell(null);

      if (result.failure !== null) {
        afterWrite(
          null,
          `${result.failure} ${result.changed} of ${bucket.leaves.length} saved before it stopped.`,
        );
        return;
      }
      afterWrite(
        `${bucket.bucketLabel}: ${result.changed} of ${result.attempted} changed for ${MATRIX_ROLE_LABELS[role]}.`,
        null,
      );
    },
    [accountId, afterWrite, busyCell, canManage, client],
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading group permissions...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load group permissions."}
      </p>
    );
  }
  if (data === null || data.status !== "ready") {
    return (
      <p className="surface__state" role="status">
        {data === null ? "Could not load group permissions." : data.reason}
      </p>
    );
  }

  return (
    <div className="operator-panel group-permissions">
      <p className="group-permissions__intro">
        What each role is allowed to do in this workspace. Changing a row changes it for every
        member holding that role. This is separate from duty tracking, mobile flags, and alert
        channels, which are settings rather than permissions and stay on their own panels.
      </p>

      <div className="group-permissions__toolbar">
        <label className="group-permissions__search">
          <span className="group-permissions__search-label">Search permissions</span>
          <input
            type="search"
            value={query}
            placeholder="scan, schedule, evidence"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="group-permissions__toolbar-actions">
          <button type="button" onClick={() => setCollapsed(new Set())}>
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(new Set(buckets.map((bucket) => bucket.bucketKey)))}
          >
            Collapse all
          </button>
        </div>
      </div>

      <p className="group-permissions__count" role="status">
        {visible.length === buckets.length
          ? `${totalLeaves} permissions in ${buckets.length} categories.`
          : `${matrixLeafCount(visible)} of ${totalLeaves} permissions match.`}
      </p>

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}

      {pendingConfirm !== null ? (
        <div className="group-permissions__confirm" role="alertdialog" aria-label="Confirm category change">
          <p className="group-permissions__confirm-text">
            {bucketConfirmMessage(pendingConfirm.bucket, pendingConfirm.role, pendingConfirm.next)}
          </p>
          <div className="group-permissions__confirm-actions">
            <button
              type="button"
              className="minter__confirm-go"
              onClick={() =>
                void toggleBucket(pendingConfirm.bucket, pendingConfirm.role, pendingConfirm.next)
              }
            >
              {pendingConfirm.next ? "Grant all" : "Remove all"}
            </button>
            <button type="button" onClick={() => setPendingBucket(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {visible.length === 0 ? (
        <p className="surface__state" role="status">
          No permission matches that search.
        </p>
      ) : (
        <>
          {/* Shell v3 board microcopy: the matrix owns its scrollbars; the app chrome never
              scrolls to reach row 40. */}
          <p className="group-permissions__pin-hint">
            Scroll inside the matrix. Permission labels and role headers stay pinned.
          </p>
          <div className="group-permissions__scroll">
          <table className="commercial__table group-permissions__table">
            <caption className="group-permissions__caption">
              Role permissions by category
            </caption>
            <thead>
              <tr>
                <th scope="col">Permission</th>
                {MATRIX_ROLES.map((role) => (
                  <th key={role} scope="col" title={MATRIX_ROLE_HINTS[role]}>
                    {MATRIX_ROLE_LABELS[role]}
                  </th>
                ))}
              </tr>
            </thead>
            {visible.map((bucket) => (
              <BucketRows
                armedRole={
                  pendingConfirm?.bucket.bucketKey === bucket.bucketKey ? pendingConfirm.role : null
                }
                bucket={bucket}
                busyCell={busyCell}
                canManage={canManage}
                collapsed={collapsed.has(bucket.bucketKey)}
                key={bucket.bucketKey}
                onArmBucket={(role, next) => {
                  setFailure(null);
                  setReceipt(null);
                  setPendingBucket({ bucketKey: bucket.bucketKey, next, role });
                }}
                onToggleCollapse={() =>
                  setCollapsed((current) => {
                    const next = new Set(current);
                    if (next.has(bucket.bucketKey)) next.delete(bucket.bucketKey);
                    else next.add(bucket.bucketKey);
                    return next;
                  })
                }
                onToggleLeaf={(permissionKey, role, next) =>
                  void toggleLeaf(bucket, permissionKey, role, next)
                }
              />
            ))}
          </table>
          </div>
        </>
      )}

      {!canManage ? (
        <p className="group-permissions__note">
          Read only. Member management permission is required to change this matrix.
        </p>
      ) : (
        <p className="group-permissions__note">{PROTECTED_LEAF_NOTE}</p>
      )}
    </div>
  );
}

function BucketRows({
  armedRole,
  bucket,
  busyCell,
  canManage,
  collapsed,
  onArmBucket,
  onToggleCollapse,
  onToggleLeaf,
}: {
  /** The role whose category toggle is armed for confirmation in this bucket, if any. */
  armedRole: MatrixRole | null;
  bucket: MatrixBucket;
  busyCell: string | null;
  canManage: boolean;
  collapsed: boolean;
  onArmBucket: (role: MatrixRole, next: boolean) => void;
  onToggleCollapse: () => void;
  onToggleLeaf: (permissionKey: string, role: MatrixRole, next: boolean) => void;
}) {
  // Any write in flight disables every cell on the surface. The handlers already refuse a second
  // write, but a control that silently does nothing when pressed is worse than a disabled one.
  const anyWriteInFlight = busyCell !== null;
  return (
    <tbody className="group-permissions__bucket">
      <tr className="group-permissions__bucket-row">
        <th scope="rowgroup">
          <button
            type="button"
            aria-expanded={!collapsed}
            className="group-permissions__disclosure"
            onClick={onToggleCollapse}
          >
            <span aria-hidden="true">{collapsed ? "+" : "-"}</span> {bucket.bucketLabel}
            <span className="group-permissions__bucket-count">{bucket.leaves.length}</span>
          </button>
        </th>
        {MATRIX_ROLES.map((role) => {
          const state = categoryState(bucket, role);
          const busy = busyCell === `${bucket.bucketKey}:*:${role}`;
          const label = categoryStateLabel(state);
          return (
            <td key={role} className={`group-permissions__category group-permissions__category--${state.kind}`}>
              {canManage ? (
                <button
                  type="button"
                  aria-label={`${state.kind === "full" ? "Remove" : "Grant"} all ${bucket.bucketLabel} permissions for ${MATRIX_ROLE_LABELS[role]}`}
                  aria-pressed={armedRole === role}
                  className="group-permissions__category-toggle"
                  disabled={anyWriteInFlight}
                  onClick={() => onArmBucket(role, state.kind !== "full")}
                >
                  {busy ? "..." : label}
                </button>
              ) : (
                <span className="group-permissions__category-static">{label}</span>
              )}
            </td>
          );
        })}
      </tr>
      {collapsed
        ? null
        : bucket.leaves.map((leaf) => (
            <tr key={leaf.permissionKey} className="group-permissions__leaf-row">
              <th scope="row">
                <span className="group-permissions__leaf-key">{leaf.permissionKey}</span>
                {leaf.description !== null ? (
                  <span className="group-permissions__leaf-desc">{leaf.description}</span>
                ) : null}
              </th>
              {MATRIX_ROLES.map((role) => {
                const granted = leaf.granted[role];
                const cellId = `${bucket.bucketKey}:${leaf.permissionKey}:${role}`;
                const busy = busyCell === cellId;
                const locked = isProtectedLeaf(role, leaf.permissionKey, false);
                return (
                  <td key={role} className="group-permissions__leaf">
                    <input
                      type="checkbox"
                      aria-label={`${leaf.permissionKey} for ${MATRIX_ROLE_LABELS[role]}`}
                      checked={granted}
                      disabled={!canManage || anyWriteInFlight || (locked && granted)}
                      title={locked && granted ? PROTECTED_LEAF_NOTE : undefined}
                      onChange={(event) => onToggleLeaf(leaf.permissionKey, role, event.target.checked)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
    </tbody>
  );
}
